import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    styled,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

const StyledButton = styled(Button)(({ theme }) => ({
    background: "#4D6BF6",
    color: "#ffffff",
    boxShadow: "none",
    border: "1px solid #4D6BF6",
    whiteSpace: "nowrap",
    fontSize: "16px",
    fontWeight: "500",
    borderRadius: "10px",
    padding: ".5vh 1vw",
    letterSpacing: "0",
    "&:hover": {
        borderColor: "#4D6BF6",
        color: "#ffffff",
        backgroundColor: "#4D6BF6",
    },
    "&:disabled": {
        opacity: 0.7,
        borderColor: "#4D6BF6",
        color: "#ffffff",
        backgroundColor: "#4D6BF6",
        cursor: "not-allowed",
    },
}));

const ErrorButton = styled(Button)(({ theme }) => ({
    background: "#DC2626",
    color: "#ffffff",
    boxShadow: "none",
    border: "1px solid #DC2626",
    whiteSpace: "nowrap",
    fontSize: "16px",
    fontWeight: "500",
    borderRadius: "10px",
    padding: ".5vh 1vw",
    letterSpacing: "0",
    "&:hover": {
        borderColor: "#DC2626",
        color: "#ffffff",
        backgroundColor: "#B91C1C",
    },
    "&:disabled": {
        opacity: 0.7,
        borderColor: "#DC2626",
        color: "#ffffff",
        backgroundColor: "#DC2626",
        cursor: "not-allowed",
    },
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
    background: "#fff",
    color: "#0D0D0E",
    boxShadow: "none",
    border: "1px solid #D7D8D9",
    whiteSpace: "nowrap",
    fontSize: "16px",
    fontWeight: "500",
    borderRadius: "10px",
    padding: ".5vh 1vw",
    letterSpacing: "0",
}));

const DeleteInclusionModal = ({
    open,
    onClose,
    inclusionData,
    onDeleteInclusion,
    requiresRoomSelection = false,
}) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedRoomId, setSelectedRoomId] = useState(null);

    useEffect(() => {
        if (!open) {
            setIsDeleting(false);
            setSelectedRoomId(null);
        }
    }, [open]);

    // Auto-select room if only one room available
    useEffect(() => {
        if (requiresRoomSelection && inclusionData?.rooms) {
            if (inclusionData.rooms.length === 1) {
                setSelectedRoomId(inclusionData.rooms[0].id);
            } else {
                setSelectedRoomId(null);
            }
        } else {
            setSelectedRoomId(null);
        }
    }, [open, inclusionData, requiresRoomSelection]);

    const handleDelete = async () => {
        if (isDeleting || !inclusionData) return;

        setIsDeleting(true);
        try {
            if (requiresRoomSelection) {
                // For By Category tab - send room_id and inclusion_name
                await onDeleteInclusion(selectedRoomId, inclusionData.name);
            } else {
                // For By Room tab - send inclusion_id
                await onDeleteInclusion(inclusionData.id);
            }
            onClose();
        } catch (error) {
            console.error("Error deleting inclusion:", error);
            setIsDeleting(false);
        }
    };

    if (!inclusionData) return null;

    const canDelete = requiresRoomSelection ? selectedRoomId !== null : true;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    padding: "8px",
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#0D0D0E",
                    paddingBottom: "16px",
                }}
            >
                Delete Inclusion
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                        Are you sure you want to delete this inclusion?
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px",
                        }}
                    >
                        <Box
                            sx={{
                                width: "45px",
                                height: "45px",
                                borderRadius: "8px",
                                border: "2px solid white",
                                boxShadow: "0 4px 2.67px rgba(0,0,0,0.15)",
                                backgroundColor: "#e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <SettingsIcon sx={{ fontSize: 24, color: "#949DAA" }} />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 500, color: "#0D0D0E", fontSize: "14px" }}
                            >
                                {inclusionData.name || "-"}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: "#949DAA", fontSize: "12px" }}
                            >
                                {inclusionData.boq_count || 0} items
                            </Typography>
                        </Box>
                    </Box>

                    {requiresRoomSelection && inclusionData?.rooms && inclusionData.rooms.length > 1 && (
                        <Box sx={{ mt: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id="room-select-label">Select Room</InputLabel>
                                <Select
                                    labelId="room-select-label"
                                    value={selectedRoomId || ""}
                                    onChange={(e) => setSelectedRoomId(e.target.value ? parseInt(e.target.value) : null)}
                                    label="Select Room"
                                >
                                    <MenuItem value="">Choose a room...</MenuItem>
                                    {inclusionData.rooms.map((room) => (
                                        <MenuItem key={room.id} value={room.id}>
                                            {room.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {!selectedRoomId && (
                                <Typography variant="caption" sx={{ color: "error.main", mt: 1 }}>
                                    Please select a room to delete the inclusion from
                                </Typography>
                            )}
                        </Box>
                    )}

                    {requiresRoomSelection && inclusionData?.rooms && inclusionData.rooms.length === 1 && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                This inclusion will be deleted from: <strong>{inclusionData.rooms[0].name}</strong>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions
                sx={{ padding: "16px 24px", justifyContent: "space-between" }}
            >
                <GreyStyledButton onClick={onClose} disabled={isDeleting}>
                    Cancel
                </GreyStyledButton>

                <ErrorButton onClick={handleDelete} disabled={!canDelete || isDeleting}>
                    {isDeleting ? (
                        <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Deleting...
                        </>
                    ) : (
                        "Delete"
                    )}
                </ErrorButton>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteInclusionModal;

